import {
  fetchFundings,
  fetchMarket,
  fetchOrderBook,
  fetchRecentTrades,
  formatPct,
  formatPrice,
  formatSize,
  formatUsdCompact,
  resolveMarketId,
} from '../../lib/lighterApi'

const POLL_MS = 4_000

function setText(el, text) {
  if (!el) return
  el.textContent = text
}

function findStatValue(statsRoot, label) {
  if (!statsRoot) return null
  const nodes = [...statsRoot.querySelectorAll('p')]
  for (const labelEl of nodes) {
    if ((labelEl.textContent || '').trim() !== label) continue
    const col = labelEl.parentElement
    const values = col?.querySelectorAll('p')
    if (!values || values.length < 2) return null
    return values[1]
  }
  return null
}

function nextFundingCountdown(now = Date.now()) {
  const d = new Date(now)
  const next = new Date(d)
  next.setUTCMinutes(0, 0, 0)
  next.setUTCHours(d.getUTCHours() + 1)
  const ms = Math.max(0, next.getTime() - now)
  const totalSec = Math.floor(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function updateOrderRow(row, level, maxSize, side) {
  if (!row || !level) {
    if (row) row.style.visibility = 'hidden'
    return
  }
  row.style.visibility = ''
  const priceEl = row.querySelector('[data-testid="price"]')
  const sizeEl = row.querySelector('[data-testid="size"]')
  const totalEl = row.querySelector('[data-testid="total-size"]')
  setText(priceEl, formatPrice(level.price, 4))
  setText(sizeEl, formatSize(level.size, 2))
  setText(totalEl, formatSize(level.size, 2))

  const bar = row.querySelector('.absolute.inset-y-0.flex')
  if (bar && maxSize > 0) {
    const pct = Math.min(100, (level.size / maxSize) * 100)
    bar.style.width = `${pct.toFixed(2)}%`
  }

  if (side === 'ask') {
    priceEl?.classList.add('text-down-pale')
    priceEl?.classList.remove('text-up-pale')
  } else {
    priceEl?.classList.add('text-up-pale')
    priceEl?.classList.remove('text-down-pale')
  }
}

function ensureTradesPanel(orderbookPanel) {
  let trades = orderbookPanel.querySelector('[data-testid="recent-trades"]')
  if (trades) return trades

  const bookBody = orderbookPanel.querySelector(
    '.relative.flex.w-full.flex-1.flex-col.overflow-hidden.font-mono'
  )
  if (!bookBody) return null

  bookBody.dataset.panelKind = 'orderbook'

  trades = document.createElement('div')
  trades.dataset.testid = 'recent-trades'
  trades.className =
    'relative hidden min-h-0 w-full flex-1 flex-col overflow-hidden font-mono'
  trades.innerHTML = `
    <div class="grid w-full shrink-0 grid-cols-[30%_30%_40%] items-center border-b-[0.5px] border-line bg-fill px-2 py-1 text-2xs/2xs text-dim uppercase">
      <span>Price</span>
      <span class="text-right">Size</span>
      <span class="text-right">Time</span>
    </div>
    <div data-testid="recent-trades-list" class="flex min-h-0 flex-1 flex-col overflow-auto"></div>
  `
  bookBody.parentElement?.insertBefore(trades, bookBody.nextSibling)
  return trades
}

function renderTrades(listEl, trades) {
  if (!listEl) return
  listEl.innerHTML = trades
    .slice(0, 40)
    .map((t) => {
      const color = t.isBuy ? 'text-up-pale' : 'text-down-pale'
      const time = new Date(t.timestamp).toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
      return `<div class="grid h-5 min-h-5 grid-cols-[30%_30%_40%] items-center px-2 text-sm/sm tabular-nums">
        <span class="${color} text-start">${formatPrice(t.price, 4)}</span>
        <span class="text-right text-ink/70">${formatSize(t.size, 2)}</span>
        <span class="text-right text-dim">${time}</span>
      </div>`
    })
    .join('')
}

function setBookTab(orderbookPanel, mode) {
  const buttons = [
    ...orderbookPanel.querySelectorAll(
      '.flex.shrink-0.items-center.justify-between button'
    ),
  ]
  const orderBtn = buttons.find((b) =>
    (b.textContent || '').includes('Order Book')
  )
  const tradesBtn = buttons.find((b) => (b.textContent || '').includes('Trades'))
  const bookBody = orderbookPanel.querySelector('[data-panel-kind="orderbook"]')
  const trades = ensureTradesPanel(orderbookPanel)

  const showTrades = mode === 'trades'
  if (bookBody) bookBody.classList.toggle('hidden', showTrades)
  if (trades) {
    trades.classList.toggle('hidden', !showTrades)
    trades.classList.toggle('flex', showTrades)
  }

  if (orderBtn) {
    orderBtn.setAttribute('aria-pressed', showTrades ? 'false' : 'true')
  }
  if (tradesBtn) {
    tradesBtn.setAttribute('aria-pressed', showTrades ? 'true' : 'false')
  }
}

/**
 * @param {HTMLElement} page
 * @param {{ symbol: string }} opts
 */
export function mountLiveMarketData(page, { symbol }) {
  let cancelled = false
  let marketId = null
  let timer = null
  let fundingRate = null
  let bookLimit = 14

  try {
    const settings = JSON.parse(
      localStorage.getItem('lighter:user-settings') || '{}'
    )
    if (settings.orderBookDepth) bookLimit = Number(settings.orderBookDepth) || 14
  } catch {
    /* ignore */
  }

  page.dataset.markPrice = page.dataset.markPrice || ''
  page.dataset.takerFee = page.dataset.takerFee || '0'
  page.dataset.makerFee = page.dataset.makerFee || '0'
  page.dataset.availableSize = page.dataset.availableSize || ''
  page.dataset.symbol = symbol

  const orderbookPanel =
    page.querySelector('[data-trade-panel="orderbook"]') ||
    page.querySelector('.react-grid-item:nth-child(3)')

  const onBookClick = (event) => {
    if (!orderbookPanel) return
    const btn = event.target.closest('button')
    if (!btn || !orderbookPanel.contains(btn)) return
    const label = (btn.textContent || '').replace(/\s+/g, ' ').trim()
    if (label === 'Order Book') setBookTab(orderbookPanel, 'orderbook')
    if (label === 'Trades') setBookTab(orderbookPanel, 'trades')
  }
  const onSettings = (event) => {
    const depth = Number(event.detail?.orderBookDepth)
    if (depth) bookLimit = depth
  }
  orderbookPanel?.addEventListener('click', onBookClick)
  window.addEventListener('lighter:settings', onSettings)
  if (orderbookPanel) ensureTradesPanel(orderbookPanel)

  async function tick() {
    if (cancelled) return
    try {
      if (marketId == null) {
        marketId = await resolveMarketId(symbol)
        if (marketId == null) throw new Error(`Unknown market ${symbol}`)
      }

      const [market, book, trades, fundings] = await Promise.all([
        fetchMarket(symbol),
        fetchOrderBook(marketId, bookLimit),
        fetchRecentTrades(marketId, 40),
        fetchFundings(marketId, 24),
      ])
      if (cancelled || !market) return

      const mark = Number(market.mark_price)
      const index = Number(market.index_price)
      const change = Number(market.daily_price_change)
      const volume = Number(market.daily_quote_token_volume)
      const oiBase = Number(market.open_interest)
      const oiUsd = Number.isFinite(oiBase) && Number.isFinite(mark)
        ? oiBase * mark
        : NaN

      page.dataset.markPrice = String(mark)
      page.dataset.takerFee = String(Number(market.taker_fee) || 0)
      page.dataset.makerFee = String(Number(market.maker_fee) || 0)
      page.dispatchEvent(
        new CustomEvent('lighter:market-tick', {
          detail: { mark, market, symbol },
        })
      )

      const stats = page.querySelector('[data-testid="stats"]')
      setText(findStatValue(stats, 'Mark Price'), formatPrice(mark, 4))
      setText(findStatValue(stats, 'Index Price'), formatPrice(index, 4))
      const changeEl = findStatValue(stats, '24h Change')
      if (changeEl) {
        changeEl.textContent = formatPct(change, 2)
        changeEl.classList.toggle('text-up', change >= 0)
        changeEl.classList.toggle('text-down', change < 0)
        changeEl.classList.toggle('text-ink', false)
      }
      setText(findStatValue(stats, '24h Volume'), formatUsdCompact(volume))
      setText(findStatValue(stats, 'Open Interest'), formatUsdCompact(oiUsd))

      const latestFunding = fundings[fundings.length - 1]
      if (latestFunding?.rate != null) {
        fundingRate = Number(latestFunding.rate)
      }
      const fundingEl = findStatValue(stats, '1hr Funding')
      if (fundingEl && Number.isFinite(fundingRate)) {
        fundingEl.textContent = `${fundingRate}%`
      }

      // Next Funding lives in the same column as 1hr Funding
      if (stats) {
        const labels = [...stats.querySelectorAll('p')]
        const nextLabel = labels.find(
          (p) => (p.textContent || '').trim() === 'Next Funding'
        )
        const nextVal = nextLabel?.parentElement?.querySelectorAll('p')?.[1]
        setText(nextVal, nextFundingCountdown())
      }

      const mobilePrice =
        page.querySelector('[data-testid="mobile-last-price"]') ||
        page.querySelector(
          '[data-testid="mobile-market-stats-toggle"] .tabular-nums'
        )
      const mobileChange =
        page.querySelector('[data-testid="mobile-last-change"]') ||
        page.querySelector(
          '[data-testid="mobile-market-stats-toggle"] .text-up, [data-testid="mobile-market-stats-toggle"] .text-down'
        )
      setText(mobilePrice, formatPrice(mark, 4))
      if (mobileChange) {
        mobileChange.textContent = formatPct(change, 2)
        mobileChange.classList.toggle('text-down', change < 0)
        mobileChange.classList.toggle('text-up', change >= 0)
      }

      const askRows = [
        ...(page.querySelectorAll('[data-testid="ob-ask"]') || []),
      ]
      const bidRows = [
        ...(page.querySelectorAll('[data-testid="ob-bid"]') || []),
      ]
      const asks = book.asks.slice(0, askRows.length)
      const bids = book.bids.slice(0, bidRows.length)
      const maxSize = Math.max(
        1,
        ...asks.map((a) => a.size),
        ...bids.map((b) => b.size)
      )

      // Asks render top→bottom high to low visually (justify-end stack); API asks are low→high.
      const asksVisual = [...asks].reverse()
      askRows.forEach((row, i) =>
        updateOrderRow(row, asksVisual[i], maxSize, 'ask')
      )
      bidRows.forEach((row, i) => updateOrderRow(row, bids[i], maxSize, 'bid'))

      const bestAsk = asks[0]?.price
      const bestBid = bids[0]?.price
      if (Number.isFinite(bestAsk) && Number.isFinite(bestBid)) {
        const spread = bestAsk - bestBid
        const spreadPct = (spread / bestBid) * 100
          const spreadLabel = [...page.querySelectorAll('span')].find(
          (el) => (el.textContent || '').trim() === 'Spread'
        )
        const spreadGrid = spreadLabel?.parentElement
        if (spreadGrid) {
          const cells = [...spreadGrid.children]
          if (cells[0]) cells[0].textContent = formatPrice(spread, 4)
          if (cells[2]) cells[2].textContent = `${spreadPct.toFixed(3)}%`
        }
      }

      const tradesList = page.querySelector('[data-testid="recent-trades-list"]')
      renderTrades(tradesList, trades)

      const blockHeight =
        trades.find((t) => t.blockHeight)?.blockHeight ||
        trades[0]?.blockHeight ||
        0
      if (blockHeight) {
        window.dispatchEvent(
          new CustomEvent('lighter:block-height', {
            detail: { height: blockHeight },
          })
        )
      }
    } catch (err) {
      console.warn('[tradeLiveData]', err)
    }
  }

  tick()
  timer = window.setInterval(tick, POLL_MS)

  return () => {
    cancelled = true
    if (timer) window.clearInterval(timer)
    orderbookPanel?.removeEventListener('click', onBookClick)
    window.removeEventListener('lighter:settings', onSettings)
  }
}
